import { Fragment, type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { IconButton } from '@/components/ui/IconButton';
import { cn } from '@/utils/cn';

export interface LineColumn<T> {
  /** Stable React key for the column. */
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  /** Width classes for the column, e.g. `w-28` or `min-w-[200px]`. */
  width?: string;
  /** Extra classes for the cell (read-only figures: font, colour). */
  className?: string;
  render: (row: T, index: number) => ReactNode;
}

interface LinesGridProps<T> {
  rows: readonly T[];
  columns: readonly LineColumn<T>[];
  rowKey: (row: T) => string;
  /** Something before the line number in the # column — e.g. the toggle of a detail row. */
  lead?: (row: T, index: number) => ReactNode;
  /**
   * A second row under a line for what does not fit the grid (a cheque's crossing, its drawer bank).
   * Return null when the line has nothing to show.
   */
  detail?: (row: T, index: number) => ReactNode;
  /** Shown when given: the remove control at the end of each row. */
  onRemove?: (row: T, index: number) => void;
  /** A document keeps at least one line, so removing the last one is refused. */
  minRows?: number;
  /** Under the grid, outside the scroll area: Add line, totals. */
  footer?: ReactNode;
}

const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

/**
 * The editable lines of a document — bill, invoice, payment, receipt (ADR-004 step 3).
 *
 * Each of the four forms drew this table by hand: the same sticky header, numbering, hover-revealed
 * delete and footer, styled four slightly different ways. The columns are what really differs, so
 * that is all a form now provides; the cells use the kit's Input and Select.
 */
export function LinesGrid<T>({
  rows, columns, rowKey, lead, detail, onRemove, minRows = 1, footer,
}: LinesGridProps<T>) {
  const span = columns.length + 1 + (onRemove ? 1 : 0);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-8">#</th>
              {columns.map(c => (
                <th
                  key={c.key}
                  className={cn(
                    'px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap',
                    alignClass[c.align ?? 'left'],
                    c.width,
                  )}
                >
                  {c.header}
                </th>
              ))}
              {onRemove && <th className="w-9" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((row, idx) => {
              const extra = detail?.(row, idx);
              return (
                <Fragment key={rowKey(row)}>
                  <tr className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-3 py-2 text-xs text-slate-400 select-none">
                      <div className="flex items-center gap-0.5">
                        {lead?.(row, idx)}
                        {idx + 1}
                      </div>
                    </td>
                    {columns.map(c => (
                      <td key={c.key} className={cn('px-3 py-1.5', alignClass[c.align ?? 'left'], c.className)}>
                        {c.render(row, idx)}
                      </td>
                    ))}
                    {onRemove && (
                      <td className="px-2 py-1.5">
                        <IconButton
                          variant="bare"
                          size="xs"
                          label={`Remove line ${idx + 1}`}
                          icon={<Trash2 size={13} />}
                          onClick={() => onRemove(row, idx)}
                          disabled={rows.length <= minRows}
                          className="hover:text-red-500 opacity-0 group-hover:opacity-100 disabled:opacity-0"
                        />
                      </td>
                    )}
                  </tr>
                  {extra && (
                    <tr className="bg-slate-50/70 dark:bg-slate-800/40" data-testid={`line-detail-${idx + 1}`}>
                      <td />
                      <td colSpan={span - 1} className="px-3 pb-2.5 pt-0.5">{extra}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer && (
        <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
          {footer}
        </div>
      )}
    </div>
  );
}
